import React, { useState, useEffect, useMemo, useRef } from "react";
import CrudService from "services/cruds-service";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import DashboardNavbar from "../../../../../../examples/Navbars/DashboardNavbar";
import DashboardLayout from "../../../../../../examples/LayoutContainers/DashboardLayout";
import Footer from "../../../../../../examples/Footer";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import MuiTooltip from "@mui/material/Tooltip";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Box from "@mui/material/Box";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PublicIcon from "@mui/icons-material/Public";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DataTable from "examples/Tables/DataTable";
import { VectorMap } from "@react-jvectormap/core";
import { worldMerc } from "@react-jvectormap/world";
import { useTheme } from "@mui/material/styles";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import countryOptions from "../../../new-product/components/List/Countries";
import { useNavigate } from "react-router-dom";
ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const shadowPlugin = {
    id: "shadow",
    beforeDatasetsDraw(chart) {
        const { ctx } = chart;
        ctx.save();
        ctx.shadowColor = "rgba(0,0,0,0.15)";
        ctx.shadowBlur = 12;
        ctx.shadowOffsetY = 6;
    },
    afterDatasetsDraw(chart) {
        chart.ctx.restore();
    },
};

const centerTextPlugin = {
    id: "centerText",
    afterDraw(chart, _args, opts) {
        if (chart.config.type !== "doughnut") return;
        const { ctx } = chart;
        const meta = chart.getDatasetMeta(0);
        if (!meta?.data?.length) return;

        const x = meta.data[0].x;
        const y = meta.data[0].y;

        const total = chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
        ctx.save();
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        ctx.font = "500 12px Inter, system-ui, -apple-system, Segoe UI, Roboto";
        ctx.fillStyle = "#8a8f98";
        ctx.fillText(opts?.title || "Total", x, y - 12);

        ctx.font = "700 18px Inter, system-ui, -apple-system, Segoe UI, Roboto";
        ctx.fillStyle = "#23262f";
        ctx.fillText(String(total), x, y + 10);
        ctx.restore();
    },
};

ChartJS.register(shadowPlugin, centerTextPlugin);

const getTooltipValue = (ctx) => {
    if (typeof ctx.parsed === "number") return ctx.parsed; // doughnut

    if (ctx.chart?.options?.indexAxis === "y") {
        // horizontal bar
        return ctx.parsed?.x ?? 0;
    }

    // vertical bar (default)
    return ctx.parsed?.y ?? 0;
};

const COUNTRY_ALIASES = {
    "U.S.A.": "United States",
    USA: "United States",
    US: "United States",
    "United States of America": "United States",
    UK: "United Kingdom",
    Türkiye: "Turkey",
    "Republic of Turkey": "Turkey",
};

const canonicalCountry = (name) => {
    if (!name) return "";
    const trimmed = String(name).trim();
    return COUNTRY_ALIASES[trimmed] || trimmed;
};

// --- Sabit koordinatlar
const COUNTRY_COORDS = {
    Germany: [51.1657, 10.4515],
    Turkey: [39.9334, 32.8597],
    "United Kingdom": [51.5074, -0.1278],
    "United States": [38.8951, -77.0364],
    France: [48.8566, 2.3522],
    Italy: [41.9028, 12.4964],
    Spain: [40.4168, -3.7038],
    Netherlands: [52.1326, 5.2913],
    Belgium: [50.8503, 4.3517],
    China: [39.9042, 116.4074],
    Japan: [35.6762, 139.6503],
    India: [28.6139, 77.2090],
};

async function fetchCountryCoordinates(countryName) {
    const q = encodeURIComponent(countryName);
    try {
        const res = await fetch(
            `${process.env.REACT_APP_API_URL}/locations/country-centroid?country=${q}`
        );
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
            const { lat, lon } = data[0];
            return [parseFloat(lat), parseFloat(lon)];
        }
        return null;
    } catch {
        return null;
    }
}
const normalize = (s) =>
    String(s || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "");

const ISO3_TO_ISO2 = {
    TUR: "TR",
    DEU: "DE",
    USA: "US",
    GBR: "GB",
    FRA: "FR",
    ITA: "IT",
    ESP: "ES",
    NLD: "NL",
    BEL: "BE",
    CHN: "CN",
    JPN: "JP",
    IND: "IN",
    CAN: "CA",
    AUS: "AU",
    BRA: "BR",
    RUS: "RU",
    SAU: "SA",
    ARE: "AE",
    EGY: "EG",
};

const MANUAL_NAME_TO_CODE = {
    turkiye: "TR",
    turkey: "TR",
    unitedstates: "US",
    unitedstatesofamerica: "US",
    usa: "US",
    uk: "GB",
    unitedkingdom: "GB",
    uae: "AE",
    russianfederation: "RU",
    russia: "RU",
    czechrepublic: "CZ",
    ivorycoast: "CI",
    cotedivoire: "CI",
};

function buildLookups(options) {
    const codeToRegion = new Map();
    const labelToCode = new Map();
    const codeToLabel = new Map();

    options.forEach((c) => {
        const code = String(c.code || "").toUpperCase();
        codeToRegion.set(code, c.region);
        codeToLabel.set(code, c.label);
        labelToCode.set(normalize(c.label), code);
    });

    Object.entries(MANUAL_NAME_TO_CODE).forEach(([k, v]) => labelToCode.set(k, v));
    return { codeToRegion, labelToCode, codeToLabel };
}

function extractCountryNames(raw) {
    if (!raw) return [];
    const out = [];
    const pushIf = (v) => {
        if (v !== undefined && v !== null && String(v).trim() !== "") out.push(String(v));
    };
    if (Array.isArray(raw)) raw.forEach((x) => out.push(...extractCountryNames(x)));
    else if (typeof raw === "object") ["country", "label", "name", "value", "title"].forEach((k) => pushIf(raw[k]));
    else pushIf(raw);

    return Array.from(
        new Set(
            out
                .map((s) => {
                    let t = String(s).trim();
                    if (t.includes(",")) {
                        const last = t
                            .split(",")
                            .map((x) => x.trim())
                            .filter(Boolean)
                            .pop();
                        if (last) t = last;
                    }
                    return t;
                })
                .filter(Boolean)
        )
    );
}

function StatCard({ icon, title, value, caption, color, onClick }) {
    return (
        <Card
            onClick={onClick}
            sx={{
                height: 110,
                borderRadius: 2,
                cursor: onClick ? "pointer" : "default",
                boxShadow: "none",
                "&:hover": onClick ? { transform: "translateY(-2px)", transition: "150ms" } : {},
            }}
        >
            <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "12px",
                        display: "grid",
                        placeItems: "center",
                        bgcolor: `${color}.50`,
                        color: `${color}.700`,
                        border: `1px solid`,
                        borderColor: `${color}.100`,
                    }}
                >
                    {icon}
                </Box>
                <Box sx={{ overflow: "hidden" }}>
                    <MDTypography variant="button" color="text" fontWeight="bold">
                        {title}
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="bold" sx={{ lineHeight: 1, mt: 0.5 }}>
                        {value}
                    </MDTypography>
                    {caption && (
                        <MDTypography variant="button" color="text" sx={{ opacity: 0.8 }}>
                            {caption}
                        </MDTypography>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
}

export function ServiceProviderDashboard({ embedded = false }) {
    const [data, setData] = useState([]);
    const [markers, setMarkers] = useState([]);
    const [showMap, setShowMap] = useState(false);
    const [notification, setNotification] = useState({ value: false, text: "" });
    const navigate = useNavigate();

    const [salesRepOptions, setSalesRepOptions] = useState([]);
    const [countrySearch, setCountrySearch] = useState(null);
    const [countryInput, setCountryInput] = useState("");
    const theme = useTheme();
    const tableAnchorRef = useRef(null);
    const meetingTableRef = useRef(null); // meeting tablosuna scroll
    function getCombinedCountries(sp) {
        const gaCountries = extractCountryNames(sp.serviceProviderCountry);
        const svcCountries = extractCountryNames(sp.operatingCountries);
        const combined = new Set([...gaCountries, ...svcCountries]);
        return Array.from(combined);
    }
    const { codeToRegion, labelToCode, codeToLabel } = useMemo(
        () => buildLookups(countryOptions),
        []
    );
    const toIso2Code = (value) => {
        if (!value) return null;
        const raw = String(value).trim();
        if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
        if (/^[A-Za-z]{3}$/.test(raw)) return ISO3_TO_ISO2[raw.toUpperCase()] || null;
        const norm1 = normalize(raw);
        const alias = canonicalCountry(raw);
        const norm2 = normalize(alias);
        return labelToCode.get(norm1) || labelToCode.get(norm2) || null;
    };
    const labelFromCode = (code) => codeToLabel.get(String(code || "").toUpperCase()) || code;

    const splitList = (v) =>
        Array.isArray(v)
            ? v.map(String).map((s) => s.trim()).filter(Boolean)
            : String(v || "")
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);

    const applyFilters = (arr, f) => {
        if (!arr?.length) return [];
        return arr.filter((ga) => {
            if (f.country) {
                const labels = getCombinedCountries(ga).map((n) => {
                    const code = toIso2Code(n);
                    return code ? labelFromCode(code) : canonicalCountry(n);
                });
                if (!labels.includes(f.country)) return false;
            }
            if (f.region) {
                const regions = getCombinedCountries(ga)
                    .map((n) => {
                        const code = toIso2Code(n);
                        return code ? codeToRegion.get(code) : null;
                    })
                    .filter(Boolean);
                if (!regions.includes(f.region)) return false;
            }
            if (f.industry) {
                const industries = splitList(ga.serviceProviderTypesGroup);
                if (!industries.some((x) => x.toLowerCase() === String(f.industry).toLowerCase())) return false;
            }
            if (f.service) {
                const services = splitList(ga.shippingPreferences);
                if (!services.some((x) => x.toLowerCase() === String(f.service).toLowerCase())) return false;
            }

            if (f.collobration) {
                const collabs = splitList(ga.byTypeOfCollaboration);
                if (!collabs.some((x) => x.toLowerCase() === String(f.collobration).toLowerCase())) return false;
            }
            return true;
        });
    };

    const [filters, setFilters] = useState({
        country: null,
        region: null,
        industry: null,
        service: null,
        collobration: null
    });
    const filteredAgents = useMemo(() => applyFilters(data, filters), [data, filters]);

    // === Yardımcılar

    // Meetings
    const [meetings, setMeetings] = useState([]);

    const normalizeName = (s) =>
        String(s || "")
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/\s+/g, " ")
            .trim();

    const getAttr = (obj, key) => obj?.attributes?.[key] ?? obj?.[key] ?? null;

    const meetingMethod = (m) =>
        getAttr(m, "meetingPlatform") || getAttr(m, "meetingType") || "Unknown";

    const fmtDate = (d) => {
        if (!d) return "-";
        const dt = new Date(d);
        if (isNaN(dt)) return "-";
        return dt.toLocaleString("tr-TR", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const hqCountryOptions = useMemo(() => {
        const set = new Set();
        data.forEach((ga) => {
            getCombinedCountries(ga).forEach((n) => {
                const code = toIso2Code(n);
                const label = code ? labelFromCode(code) : canonicalCountry(n);
                if (label) set.add(label);
            });
        });
        markers.forEach((m) => m?.name && set.add(m.name));
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [data, markers]);

    // === DATA FETCH
    useEffect(() => {
        (async () => {
            try {
                const response = await CrudService.getServiceProviders();
                const normalized = response.data.map((item) => ({
                    id: item.id,
                    ...item.attributes,
                }));
                setData(normalized);
            } catch (error) {
                console.error("Error fetching sps:", error);
                setNotification({
                    value: true,
                    text: "Global Agents yüklenemedi. Lütfen daha sonra tekrar deneyin.",
                });
            }
        })();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const resp = await CrudService.getMeetings();
                const list = Array.isArray(resp?.data) ? resp.data : [];
                const normalized = list.map((m) => {
                    const meetingDate = getAttr(m, "meetingDate");
                    return {
                        id: m.id,
                        customer: getAttr(m, "customer"),
                        meetingDate: meetingDate ? new Date(meetingDate) : null,
                        meetingType: getAttr(m, "meetingType"),
                        meetingPlatform: getAttr(m, "meetingPlatform"),
                        meetingStatus: getAttr(m, "meetingStatus"),
                        meetingTitle: getAttr(m, "meetingTitle"),
                        salesRep: getAttr(m, "salesRep"),
                    };
                });
                setMeetings(normalized);
            } catch (e) {
                console.error("Error fetching meetings:", e);
                setNotification({
                    value: true,
                    text: "Meetings verileri yüklenemedi. Lütfen daha sonra tekrar deneyin.",
                });
            }
        })();
    }, []);

    // Marker üretimi
    useEffect(() => {
        if (!data.length) return;
        (async () => {
            const unique = new Set();
            data.forEach((ga) => {
                const names = getCombinedCountries(ga);
                names.forEach((n) => unique.add(n));
            });

            const countries = Array.from(unique);
            const built = [];
            for (const n of countries) {
                const code = toIso2Code(n);
                const displayLabel = code ? labelFromCode(code) : canonicalCountry(n);
                const lookupKey = canonicalCountry(displayLabel);

                let coords = COUNTRY_COORDS[lookupKey];
                if (!coords) {
                    const fetched = await fetchCountryCoordinates(displayLabel);
                    if (fetched) {
                        COUNTRY_COORDS[lookupKey] = fetched;
                        coords = fetched;
                    }
                }
                if (coords) built.push({ name: displayLabel, latLng: coords });
            }
            setMarkers(built);
        })();
    }, [data]); // eslint-disable-line

    // === Gradyan yardımcıları + güvenli renkler ===
    const safe = (color, fb) => (typeof color === "string" && color ? color : fb);
    const primaryMain = safe(theme?.palette?.primary?.main, "#3B82F6");
    const primaryLight = safe(theme?.palette?.primary?.light, "#93C5FD");

    const makeVerticalGradient = (start, end) => (context) => {
        const { chart } = context;
        const { ctx, chartArea } = chart;
        const s = safe(start, "#93C5FD");
        const e = safe(end, "#3B82F6");
        if (!chartArea) return s;
        const g = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
        g.addColorStop(0, s);
        g.addColorStop(1, e);
        return g;
    };

    const c = {
        blue: { from: primaryLight, to: primaryMain },
        amber: { from: "#FFD166", to: "#F4A261" },
        green: { from: "#5DE2A5", to: "#2BB673" },
        purple: { from: "#C6A3FF", to: "#7C8CFF" },
        red: { from: "#FF9AA2", to: "#FF6B6B" },
        gray: { from: "#D7DBE0", to: "#A9B0B8" },
    };

    const donutColors = ["#4F46E5", "#22D3EE", "#60D394", "#F59E0B", "#EF4444", "#A78BFA", "#FB7185", "#10B981"];

    // ===================== CHART DATA =====================
    const hqChart = useMemo(() => {
        const countByCode = new Map();
        filteredAgents.forEach((ga) => {
            getCombinedCountries(ga).forEach((v) => {
                const code = toIso2Code(v);
                if (!code) return;
                countByCode.set(code, (countByCode.get(code) || 0) + 1);
            });
        });

        const pairs = Array.from(countByCode.entries()).sort((a, b) => b[1] - a[1]).slice(0, 10);
        const labels = pairs.map(([code]) => labelFromCode(code));
        const values = pairs.map(([, v]) => v);

        return {
            labels,
            datasets: [
                {
                    label: "HQ Count",
                    data: values,
                    backgroundColor: makeVerticalGradient(c.blue.from, c.blue.to),
                    borderColor: "transparent",
                    borderRadius: 10,
                    borderSkipped: false,
                    maxBarThickness: 36,
                    hoverBackgroundColor: makeVerticalGradient("#6FA8FF", "#3B82F6"),
                },
            ],
        };
    }, [filteredAgents]);

    const regionChart = useMemo(() => {
        const REGION_ORDER = [
            "North America",
            "Latin America",
            "Europe",
            "Middle East & North Africa (MENA)",
            "Sub-Saharan Africa",
            "CIS & Central Asia",
            "Asia-Pacific (APAC)",
            "Other/Unknown",
        ];
        const counts = new Map(REGION_ORDER.map((r) => [r, 0]));

        filteredAgents.forEach((ga) => {
            getCombinedCountries(ga).forEach((v) => {
                const code = toIso2Code(v);
                const region = code ? codeToRegion.get(code) : null;
                const key = region || "Other/Unknown";
                counts.set(key, (counts.get(key) || 0) + 1);
            });
        });

        const labels = REGION_ORDER;
        const values = labels.map((k) => counts.get(k) || 0);

        return {
            labels,
            datasets: [
                {
                    label: "HQ by Region",
                    data: values,
                    backgroundColor: labels.map((_, i) => donutColors[i % donutColors.length]),
                    borderColor: "transparent",
                    hoverOffset: 8,
                    spacing: 3,
                },
            ],
        };
    }, [filteredAgents, codeToRegion]);

    const industryChart = useMemo(() => {
        const counts = new Map();
        filteredAgents.forEach((ga) => {
            const v = ga.serviceProviderTypesGroup;
            if (!v) return;
            if (Array.isArray(v)) {
                v.forEach((x) => {
                    const key = String(x).trim();
                    if (!key) return;
                    counts.set(key, (counts.get(key) || 0) + 1);
                });
            } else {
                String(v)
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .forEach((key) => counts.set(key, (counts.get(key) || 0) + 1));
            }
        });

        const pairs = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 10);
        const labels = pairs.map(([k]) => k);
        const values = pairs.map(([, v]) => v);

        return {
            labels,
            datasets: [
                {
                    label: "Group Count",
                    data: values,
                    backgroundColor: makeVerticalGradient(c.purple.from, c.purple.to),
                    borderColor: "transparent",
                    borderRadius: 10,
                    borderSkipped: false,
                    maxBarThickness: 36,
                    hoverBackgroundColor: makeVerticalGradient("#D1B3FF", "#7C8CFF"),
                },
            ],
        };
    }, [filteredAgents]);

    const servicesChart = useMemo(() => {
        const counts = new Map();
        filteredAgents.forEach((ga) => {
            const v = ga.shippingPreferences;
            if (!v) return;
            if (Array.isArray(v)) {
                v.forEach((x) => {
                    const key = String(x).trim();
                    if (!key) return;
                    counts.set(key, (counts.get(key) || 0) + 1);
                });
            } else {
                String(v)
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .forEach((key) => counts.set(key, (counts.get(key) || 0) + 1));
            }
        });

        const pairs = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 10);
        const labels = pairs.map(([k]) => k);
        const values = pairs.map(([, v]) => v);

        return {
            labels,
            datasets: [
                {
                    label: "Service Count",
                    data: values,
                    backgroundColor: makeVerticalGradient(c.green.from, c.green.to),
                    borderColor: "transparent",
                    borderRadius: 10,
                    borderSkipped: false,
                    maxBarThickness: 36,
                },
            ],
        };
    }, [filteredAgents]);

    const collobrationChart = useMemo(() => {
        const counts = new Map();
        filteredAgents.forEach((ga) => {
            const v = ga.byTypeOfCollaboration;
            if (!v) return;
            if (Array.isArray(v)) {
                v.forEach((x) => {
                    const key = String(x).trim();
                    if (!key) return;
                    counts.set(key, (counts.get(key) || 0) + 1);
                });
            } else {
                String(v)
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .forEach((key) => counts.set(key, (counts.get(key) || 0) + 1));
            }
        });

        const pairs = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 10);
        const labels = pairs.map(([k]) => k);
        const values = pairs.map(([, v]) => v);

        return {
            labels,
            datasets: [
                {
                    label: "Collaboration Count",
                    data: values,
                    backgroundColor: makeVerticalGradient(c.red.from, c.red.to),
                    borderColor: "transparent",
                    borderRadius: 10,
                    borderSkipped: false,
                    maxBarThickness: 36,
                },
            ],
        };
    }, [filteredAgents]);


    const chartOptionsBar = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            layout: { padding: { top: 8, right: 8, bottom: 8, left: 8 } },
            animation: { duration: 700, easing: "easeOutQuart" },
            plugins: {
                legend: { display: false },
                tooltip: {
                    enabled: true,
                    displayColors: false,
                    backgroundColor: "#1F2937",
                    titleColor: "#fff",
                    bodyColor: "#fff",
                    padding: 12,
                    borderWidth: 0,
                    callbacks: {
                        title: (items) => (items?.length ? items[0].label : ""),
                        label: (ctx) => `${getTooltipValue(ctx)} service provider`,
                    },
                },
            },
            scales: {
                x: {
                    grid: { color: "rgba(0,0,0,0.06)", drawBorder: false },
                    ticks: {
                        color: "#5f6b7a",
                        autoSkip: true,
                        maxRotation: 0,
                        minRotation: 0,
                    },
                },
                y: {
                    grid: { color: "rgba(0,0,0,0.06)", drawBorder: false },
                    ticks: { color: "#5f6b7a", beginAtZero: true, precision: 0 },
                },
            },
            onHover: (evt, el) => {
                evt.native.target.style.cursor = el?.length ? "pointer" : "default";
            },
        }),
        []
    );



    const chartOptionsBarHorizontal = useMemo(
        () => ({
            ...chartOptionsBar,
            indexAxis: "y",
            scales: {
                y: { grid: { color: "rgba(0,0,0,0.06)", drawBorder: false }, ticks: { color: "#5f6b7a", mirror: false } },
                x: { grid: { color: "rgba(0,0,0,0.06)", drawBorder: false }, ticks: { color: "#5f6b7a", beginAtZero: true, precision: 0 } },
            },
        }),
        [chartOptionsBar]
    );

    const chartOptionsDoughnut = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            cutout: "68%",
            animation: { animateRotate: true, animateScale: true, duration: 800 },
            plugins: {
                legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 10, boxHeight: 10, padding: 14 } },
                centerText: { title: "Total" },
                tooltip: {
                    enabled: true,
                    displayColors: false,
                    backgroundColor: "#1F2937",
                    titleColor: "#fff",
                    bodyColor: "#fff",
                    padding: 12,
                    callbacks: {label: (ctx) => `${getTooltipValue(ctx)} service provider`},
                },
            },
        }),
        []
    );

    const hasAnyFilter = !!(filters.country || filters.region || filters.industry || filters.service || filters.collobration);

    const toggleFilter = (key, value) => {
        setFilters((prev) => {
            const same = prev[key] === value;
            const next = { ...prev, [key]: same ? null : value };
            if (key === "country" && value) next.region = null;
            return next;
        });
    };

    const clearAllFilters = () => setFilters({ country: null, region: null, industry: null, service: null, collobration: null });

    useEffect(() => {
        if (hasAnyFilter && tableAnchorRef.current) {
            tableAnchorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [hasAnyFilter]);

    const clickEditHandler = (id) => {
        navigate(`/service-provider/create-service-provider/details?id=${id}`);
    };
    const clickEditMeetingHandler = (id) => {
        navigate(`/meeting/create-meeting/details?id=${id}`);
    };
    const getRows = (data) => {
        return data.map((sp) => {
            const nameText = sp.name || "";
            const displayText = nameText.length > 40 ? `${nameText.slice(0, 37)}...` : nameText;
            const clickable = !!sp.id && !!nameText;

            return {
                id: sp.id,
                name: clickable ? (
                    <MuiTooltip title={nameText} placement="top" arrow>
            <span
                role="link"
                tabIndex={0}
                onClick={() => clickEditHandler(sp.id)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        clickEditHandler(sp.id);
                    }
                }}
                style={{
                    color: "#1e88e5",
                    textDecoration: "underline",
                    cursor: "pointer",
                    display: "inline-block",
                    maxWidth: 320,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
            >
              {displayText || "-"}
            </span>
                    </MuiTooltip>
                ) : (
                    <span>{displayText || "-"}</span>
                ),
                paymentTerms: sp.paymentTerms,
                globalAgentStatus: sp.globalAgentStatus,
                globalAgentCountry: getCombinedCountries(sp).join(", "),
                shippingPreferences: Array.isArray(sp.shippingPreferences) ? sp.shippingPreferences.join(", ") : "",
                serviceProviderTypesGroup: Array.isArray(sp.serviceProviderTypesGroup) ? sp.serviceProviderTypesGroup.join(", ") : "",
                allContactInfos: Array.isArray(sp.representatives)
                    ? sp.representatives.map((rep) => rep?.contactInfo).filter(Boolean).join(", ")
                    : "",
            };
        });
    };

    const columns = useMemo(
        () => [
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Service Provider</span>, accessor: "name" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Payment Terms (days)</span>, accessor: "paymentTerms" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Status</span>, accessor: "globalAgentStatus" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>HQ/Service Country</span>, accessor: "globalAgentCountry" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Service</span>, accessor: "shippingPreferences" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Group</span>, accessor: "serviceProviderTypesGroup" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Email</span>, accessor: "allContactInfos" },
        ],
        []
    );

    const tableRows = useMemo(() => getRows(filteredAgents), [filteredAgents, salesRepOptions]);
    const dataTableData = useMemo(() => ({ columns, rows: tableRows }), [columns, tableRows]);

    // ===================== Service Provider Meeting Status Aggregation =====================
    const agentMeetingStats = useMemo(() => {
        if (!data?.length || !meetings?.length) return [];

        const byName = new Map(data.filter((ga) => ga?.name).map((ga) => [normalizeName(ga.name), ga]));
        const buckets = new Map(); // key: ga.id

        for (const m of meetings) {
            const cust = normalizeName(m.customer);
            const ga = byName.get(cust);
            if (!ga) continue;

            const key = ga.id;
            if (!buckets.has(key)) {
                buckets.set(key, {
                    agentId: ga.id,
                    agentName: ga.name,
                    totalMeetings: 0,
                    lastMeetingDate: null,
                    lastMeetingMethod: "Unknown",
                    lastMeetingStatus: "Unknown",
                    lastMeetingTitle: null,
                    lastMeetingPlatform: null,
                });
            }

            const b = buckets.get(key);
            b.totalMeetings += 1;

            if (m.meetingDate && (!b.lastMeetingDate || m.meetingDate > b.lastMeetingDate)) {
                b.lastMeetingId = m.id;
                b.lastMeetingDate = m.meetingDate;
                b.lastMeetingMethod = meetingMethod(m);
                b.lastMeetingStatus = m.meetingStatus || "Unknown";
                b.lastMeetingTitle = m.meetingTitle || null;
                b.lastMeetingPlatform = m.meetingPlatform || null;
            }
        }

        const now = Date.now();
        for (const b of buckets.values()) {
            b.daysSince = b.lastMeetingDate ? Math.floor((now - b.lastMeetingDate.getTime()) / 86400000) : null;
        }

        return Array.from(buckets.values()).sort((a, z) => {
            const aD = a.daysSince ?? -1;
            const zD = z.daysSince ?? -1;
            return zD - aD;
        });
    }, [data, meetings]);

    // ➕ Meeting section filtreleri
    const [meetingFilters, setMeetingFilters] = useState({
        agentIds: [],
        method: null,
        status: null,
        month: null,   // YYYY-MM
        daysBand: null // "0-30" | "90+" | "nodate"
    });

    const hasMeetingFilter =
        meetingFilters.agentIds.length ||
        meetingFilters.method ||
        meetingFilters.status ||
        meetingFilters.month ||
        meetingFilters.daysBand;

    const toggleMeetingFilter = (key, value) => {
        setMeetingFilters((prev) => {
            if (key === "agentIds") {
                const exists = prev.agentIds.includes(value);
                const agentIds = exists ? prev.agentIds.filter((id) => id !== value) : [...prev.agentIds, value];
                return { ...prev, agentIds };
            }
            return { ...prev, [key]: prev[key] === value ? null : value };
        });
    };

    const clearMeetingFilters = () =>
        setMeetingFilters({ agentIds: [], method: null, status: null, month: null, daysBand: null });

    // Filtreli meeting listesi (Total Meeting için)
    const filteredMeetingsForTotal = useMemo(() => {
        const gaNameToId = new Map(data.filter((ga) => ga?.name).map((ga) => [normalizeName(ga.name), ga.id]));
        return meetings.filter((m) => {
            const id = gaNameToId.get(normalizeName(m.customer || ""));
            if (!id) return false;

            if (meetingFilters.agentIds.length && !meetingFilters.agentIds.includes(id)) return false;
            if (meetingFilters.method && meetingMethod(m) !== meetingFilters.method) return false;
            if (meetingFilters.status && (m.meetingStatus || "Unknown") !== meetingFilters.status) return false;
            if (meetingFilters.month) {
                if (!(m.meetingDate instanceof Date) || isNaN(m.meetingDate)) return false;
                const y = m.meetingDate.getFullYear();
                const mm = String(m.meetingDate.getMonth() + 1).padStart(2, "0");
                const key = `${y}-${mm}`;
                if (key !== meetingFilters.month) return false;
            }
            return true;
        });
    }, [meetings, data, meetingFilters]);

    // Meeting Status tablosu için filtreli stats
    const filteredMeetingStats = useMemo(() => {
        const arr = agentMeetingStats || [];
        const { agentIds, method, status, month, daysBand } = meetingFilters;
        if (!arr.length) return [];

        return arr.filter((s) => {
            if (agentIds.length && !agentIds.includes(s.agentId)) return false;
            if (method && (s.lastMeetingMethod || "Unknown") !== method) return false;
            if (status && (s.lastMeetingStatus || "Unknown") !== status) return false;
            if (month) {
                const key =
                    s.lastMeetingDate instanceof Date && !isNaN(s.lastMeetingDate)
                        ? `${s.lastMeetingDate.getFullYear()}-${String(s.lastMeetingDate.getMonth() + 1).padStart(2, "0")}`
                        : null;
                if (key !== month) return false;
            }
            if (daysBand === "0-30") {
                if (!(typeof s.daysSince === "number" && s.daysSince <= 30)) return false;
            } else if (daysBand === "90+") {
                if (!(typeof s.daysSince === "number" && s.daysSince >= 90)) return false;
            } else if (daysBand === "nodate") {
                if (s.lastMeetingDate) return false;
            }
            return true;
        });
    }, [agentMeetingStats, meetingFilters]);

    // KPI'lar
    const meetingKpis = useMemo(() => {
        const totalMeetings = filteredMeetingsForTotal.length;

        const withDays = filteredMeetingStats.filter((s) => typeof s.daysSince === "number");
        const avgDays =
            withDays.length > 0 ? Math.round(withDays.reduce((acc, s) => acc + s.daysSince, 0) / withDays.length) : 0;

        const count0_30 = filteredMeetingStats.filter((s) => typeof s.daysSince === "number" && s.daysSince <= 30).length;
        const count90p = filteredMeetingStats.filter((s) => typeof s.daysSince === "number" && s.daysSince >= 90).length;
        const countNoDate = filteredMeetingStats.filter((s) => !s.lastMeetingDate).length;

        return { totalMeetings, avgDays, count0_30, count90p, countNoDate };
    }, [filteredMeetingsForTotal, filteredMeetingStats]);

    // Grafik dataset'leri (Meeting section) — Customer Distribution (Top 15 by total meetings)
    const customerDistributionChart = useMemo(() => {
        const top = [...agentMeetingStats]
            .sort((a, z) => (z.totalMeetings ?? 0) - (a.totalMeetings ?? 0))
            .slice(0, 15);
        return {
            labels: top.map((x) => x.agentName),
            datasets: [
                {
                    label: "Meetings",
                    data: top.map((x) => x.totalMeetings ?? 0),
                    backgroundColor: makeVerticalGradient(c.amber.from, c.amber.to),
                    borderColor: "transparent",
                    borderRadius: 10,
                    borderSkipped: false,
                    maxBarThickness: 36,
                },
            ],
        };
    }, [agentMeetingStats]);

    const { methodCounts, statusCounts, monthlyCounts } = useMemo(() => {
        const method = new Map();
        const status = new Map();
        const monthly = new Map(); // key: YYYY-MM

        const gaNames = new Set(data.filter((ga) => ga?.name).map((ga) => normalizeName(ga.name)));

        for (const m of meetings) {
            if (!m.customer || !gaNames.has(normalizeName(m.customer))) continue;

            const meth = meetingMethod(m);
            method.set(meth, (method.get(meth) || 0) + 1);

            const st = m.meetingStatus || "Unknown";
            status.set(st, (status.get(st) || 0) + 1);

            if (m.meetingDate instanceof Date && !isNaN(m.meetingDate)) {
                const y = m.meetingDate.getFullYear();
                const mm = String(m.meetingDate.getMonth() + 1).padStart(2, "0");
                const key = `${y}-${mm}`;
                monthly.set(key, (monthly.get(key) || 0) + 1);
            }
        }

        return { methodCounts: method, statusCounts: status, monthlyCounts: monthly };
    }, [data, meetings]);

    const methodChart = useMemo(() => {
        const entries = Array.from(methodCounts.entries());
        return {
            labels: entries.map(([k]) => k),
            datasets: [
                {
                    label: "Number Meeting",
                    data: entries.map(([, v]) => v),
                    backgroundColor: entries.map((_, i) => donutColors[i % donutColors.length]),
                    borderColor: "transparent",
                    hoverOffset: 8,
                    spacing: 3,
                },
            ],
        };
    }, [methodCounts]);

    const statusChart = useMemo(() => {
        const entries = Array.from(statusCounts.entries());
        return {
            labels: entries.map(([k]) => k),
            datasets: [
                {
                    label: "Total Meetings",
                    data: entries.map(([, v]) => v),
                    backgroundColor: makeVerticalGradient(c.gray.from, c.gray.to),
                    borderColor: "transparent",
                    borderRadius: 10,
                    borderSkipped: false,
                    maxBarThickness: 36,
                },
            ],
        };
    }, [statusCounts]);

    const monthlyChart = useMemo(() => {
        const keys = Array.from(monthlyCounts.keys()).sort();
        return {
            labels: keys,
            datasets: [
                {
                    label: "Monthly Meeting",
                    data: keys.map((k) => monthlyCounts.get(k)),
                    backgroundColor: makeVerticalGradient(c.blue.from, c.blue.to),
                    borderColor: "transparent",
                    borderRadius: 10,
                    borderSkipped: false,
                    maxBarThickness: 36,
                },
            ],
        };
    }, [monthlyCounts]);

    // --------- Özet Tablo (agent seviyesinde tek satır)
    const meetingStatusColumns = useMemo(
        () => [
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Service Provider</span>, accessor: "serviceprovider" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Title</span>, accessor: "title" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Last Meeting</span>, accessor: "lastDate" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Days</span>, accessor: "daysSince" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Method</span>, accessor: "method" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Status</span>, accessor: "status" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Total</span>, accessor: "total" },
        ],
        []
    );

    const meetingStatusRows = useMemo(() => {
        return filteredMeetingStats.map((s) => {
            const displayText = s.agentName?.length > 40 ? `${s.agentName.slice(0, 37)}...` : s.agentName || "-";
            const clickable = !!s.agentId && !!s.agentName;

            return {
                id: s.agentId,
                serviceprovider: clickable ? (
                    <MuiTooltip title={s.agentName} placement="top" arrow>
            <span
                role="link"
                tabIndex={0}
                onClick={() => clickEditHandler(s.agentId)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        clickEditHandler(s.agentId);
                    }
                }}
                style={{
                    color: "#1e88e5",
                    textDecoration: "underline",
                    cursor: "pointer",
                    display: "inline-block",
                    maxWidth: 320,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
            >
              {displayText}
            </span>
                    </MuiTooltip>
                ) : (
                    <span>{displayText}</span>
                ),
                lastDate: fmtDate(s.lastMeetingDate),
                daysSince: s.daysSince ?? "-",
                method: s.lastMeetingMethod || "-",
                status: s.lastMeetingStatus || "-",
                total: s.totalMeetings ?? 0,
                title: s.lastMeetingTitle ? (
                    <MuiTooltip title="Edit meeting" placement="top" arrow>
    <span
        role="link"
        tabIndex={0}
        onClick={() => clickEditMeetingHandler(s.lastMeetingId)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); clickEditMeetingHandler(s.lastMeetingId); } }}
        style={{ color: "#ff9800", textDecoration: "underline", cursor: "pointer" }}
    >
      {s.lastMeetingTitle}
    </span>
                    </MuiTooltip>
                ) : "-",
            };
        });
    }, [filteredMeetingStats]);

    // --------- Detay Tablo (meeting seviyesinde N satır)
    const meetingDetailColumns = useMemo(
        () => [
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Service Provider</span>, accessor: "serviceprovider" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Title</span>, accessor: "title" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Date</span>, accessor: "date" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Method</span>, accessor: "method" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Status</span>, accessor: "status" },
        ],
        []
    );

    const idByName = useMemo(() => {
        return new Map(data.filter((ga) => ga?.name).map((ga) => [normalizeName(ga.name), ga.id]));
    }, [data]);

    const nameById = useMemo(() => {
        return new Map(data.filter((ga) => ga?.id && ga?.name).map((ga) => [ga.id, ga.name]));
    }, [data]);

    const meetingDetailRows = useMemo(() => {
        return filteredMeetingsForTotal.map((m, i) => {
            const agentId = idByName.get(normalizeName(m.customer || ""));
            const agentName = (agentId && nameById.get(agentId)) || m.customer || "-";
            const clickable = !!agentId && !!agentName;

            return {
                id: `${m.id || i}_${m.meetingDate ? +m.meetingDate : "nd"}`,
                serviceprovider: clickable ? (
                    <MuiTooltip title={agentName} placement="top" arrow>
            <span
                role="link"
                tabIndex={0}
                onClick={() => clickEditHandler(agentId)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); clickEditHandler(agentId); } }}
                style={{ color: "#1e88e5", textDecoration: "underline", cursor: "pointer", display: "inline-block", maxWidth: 320, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {agentName}
            </span>
                    </MuiTooltip>
                ) : (<span>{agentName}</span>),
                title: m.meetingTitle ? (
                    <MuiTooltip title="Edit meeting" placement="top" arrow>
    <span
        role="link"
        tabIndex={0}
        onClick={() => clickEditMeetingHandler(m.id)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); clickEditMeetingHandler(m.id); } }}
        style={{ color: "#ff9800", textDecoration: "underline", cursor: "pointer" }}
    >
      {m.meetingTitle}
    </span>
                    </MuiTooltip>
                ) : "-",
                date: fmtDate(m.meetingDate),
                method: meetingMethod(m),
                status: m.meetingStatus || "Unknown",
            };
        });
    }, [filteredMeetingsForTotal, idByName, nameById]);

    const hqOptionsWithClick = useMemo(
        () => ({
            ...chartOptionsBar,
            onClick: (evt, elements, chart) => {
                if (!elements?.length) return;
                const idx = elements[0].index;
                const label = chart.data.labels[idx];
                toggleFilter("country", label);
            },
            scales: {
                ...chartOptionsBar.scales,
                x: {
                    ...chartOptionsBar.scales.x,
                    ticks: {
                        ...chartOptionsBar.scales.x.ticks,
                        callback: function (val) {
                            const label = this.getLabelForValue(val);
                            return label && label.length > 5 ? label.slice(0, 5) + "…" : label;
                        },
                    },
                },
            },
        }),
        [chartOptionsBar]
    );


    const regionOptionsWithClick = useMemo(
        () => ({
            ...chartOptionsDoughnut,
            onClick: (evt, elements, chart) => {
                if (!elements?.length) return;
                const idx = elements[0].index;
                const label = chart.data.labels[idx];
                toggleFilter("region", label);
            },
        }),
        [chartOptionsDoughnut]
    );

    const industryOptionsWithClick = useMemo(
        () => ({
            ...chartOptionsBarHorizontal,
            onClick: (evt, elements, chart) => {
                if (!elements?.length) return;
                const idx = elements[0].index;
                const label = chart.data.labels[idx];
                toggleFilter("industry", label);
            },
        }),
        [chartOptionsBarHorizontal]
    );

    const servicesOptionsWithClick = useMemo(
        () => ({
            ...chartOptionsBarHorizontal,
            onClick: (evt, elements, chart) => {
                if (!elements?.length) return;
                const idx = elements[0].index;
                const label = chart.data.labels[idx];
                toggleFilter("service", label);
            },
        }),
        [chartOptionsBarHorizontal]
    );

    const collobrationOptionsWithClick = useMemo(
        () => ({
            ...chartOptionsBarHorizontal,
            onClick: (evt, elements, chart) => {
                if (!elements?.length) return;
                const idx = elements[0].index;
                const label = chart.data.labels[idx];
                toggleFilter("collobration", label);
            },
        }),
        [chartOptionsBarHorizontal]
    );


    const customerDistributionOptionsWithClick = useMemo(
        () => ({
            ...chartOptionsBarHorizontal,

            // 👉 LABEL KIRPMA (30 karakter)
            scales: {
                ...chartOptionsBarHorizontal.scales,
                y: {
                    ...chartOptionsBarHorizontal.scales.y,
                    ticks: {
                        ...chartOptionsBarHorizontal.scales.y.ticks,
                        callback: function (value) {
                            const label = this.getLabelForValue(value);
                            if (!label) return "";
                            return label.length > 30 ? label.slice(0, 30) + "…" : label;
                        },
                    },
                },
            },

            // 👉 TOOLTIP = FULL NAME
            plugins: {
                ...chartOptionsBarHorizontal.plugins,
                tooltip: {
                    ...chartOptionsBarHorizontal.plugins.tooltip,
                    callbacks: {
                        title: (items) => {
                            if (!items?.length) return "";
                            return items[0].label; // FULL NAME
                        },
                        label: (ctx) => `${getTooltipValue(ctx)} meetings`,
                    },
                },
            },

            // 👉 CLICK FILTER
            onClick: (evt, elements, chart) => {
                if (!elements?.length) return;
                const idx = elements[0].index;
                const label = chart.data.labels[idx]; // FULL NAME
                const row = agentMeetingStats.find((x) => x.agentName === label);
                if (row?.agentId) {
                    toggleMeetingFilter("agentIds", row.agentId);
                    meetingTableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                }
            },
        }),
        [chartOptionsBarHorizontal, agentMeetingStats]
    );


    const methodOptionsWithClick = useMemo(
        () => ({
            ...chartOptionsDoughnut,
            onClick: (evt, elements, chart) => {
                if (!elements?.length) return;
                const idx = elements[0].index;
                const label = chart.data.labels[idx]; // method
                toggleMeetingFilter("method", label);
                meetingTableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            },
        }),
        [chartOptionsDoughnut]
    );

    const statusOptionsWithClick = useMemo(
        () => ({
            ...chartOptionsBar,
            onClick: (evt, elements, chart) => {
                if (!elements?.length) return;
                const idx = elements[0].index;
                const label = chart.data.labels[idx]; // status
                toggleMeetingFilter("status", label);
                meetingTableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            },
        }),
        [chartOptionsBar]
    );

    const monthlyOptionsWithClick = useMemo(
        () => ({
            ...chartOptionsBar,
            onClick: (evt, elements, chart) => {
                if (!elements?.length) return;
                const idx = elements[0].index;
                const label = chart.data.labels[idx]; // YYYY-MM
                toggleMeetingFilter("month", label);
                meetingTableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            },
        }),
        [chartOptionsBar]
    );

    // --- Detay tablosu modunu belirle
    const showDetailTable = hasMeetingFilter;


    const content = (
        <>
            {!embedded && <DashboardNavbar />}

            {notification.value && (
                <MDAlert color="info" my="20px">
                    <MDTypography variant="body2" color="white">
                        {notification.text}
                    </MDTypography>
                </MDAlert>
            )}

            <MDBox pt={6} pb={3}>
                <MDBox mb={3}>
                    <MDBox
                        p={1}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        gap={2}
                        flexWrap="wrap"
                    >
                        <MDTypography variant="h5" fontWeight="medium">
                            SUPPLIER MANAGEMENT DASHBOARD
                        </MDTypography>

                        <Button
                            variant={showMap ? "contained" : "outlined"}
                            size="small"
                            onClick={() => setShowMap((prev) => !prev)}
                            startIcon={<PublicIcon />}
                            endIcon={showMap ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            sx={{
                                height: 34,
                                borderRadius: "12px",
                                textTransform: "none",
                                fontWeight: 700,
                                px: 1.8,
                                color: showMap ? "#fff" : "primary.main",
                                background: showMap
                                    ? "#1976d2"
                                    : "#fff",
                                borderColor: "primary.main",
                                "&:hover": {
                                    color: showMap ? "#fff" : "primary.dark",
                                    background: showMap
                                        ? "#1565c0"
                                        : "#f8fafc",
                                },
                            }}
                        >
                            {showMap ? "Hide Map" : "Mapping"}
                        </Button>
                    </MDBox>
                </MDBox>

                {/* HARİTA */}
                {showMap && (
                    <Grid container spacing={2}>
                        {/* SOL: HARİTA (%75) */}
                        <Grid item xs={12} md={9} sx={{ height: { xs: "60vh", md: "72vh" } }}>
                            <VectorMap
                                key={`m-${markers.length}`}
                                map={worldMerc}
                                zoomOnScroll={false}
                                zoomButtons={false}
                                markers={markers}
                                backgroundColor="transparent"
                                containerStyle={{ width: "100%", height: "100%" }}
                                regionStyle={{ initial: { fill: "#dee2e7" } }}
                                markerStyle={{
                                    initial: {
                                        r: 7,
                                        fill: "#e91e63",
                                        stroke: "#ffffff",
                                        "stroke-width": 5,
                                        "stroke-opacity": 0.5,
                                    },
                                }}
                                onMarkerClick={(e, index) => {
                                    const m = markers[index];
                                    if (m?.name) {
                                        setCountrySearch(m.name);
                                        setCountryInput(m.name);
                                        toggleFilter("country", m.name);
                                    }
                                }}
                            />
                        </Grid>

                        {/* SAĞ: FİLTRE PANELİ (%25) */}
                        <Grid item xs={12} md={3}>
                            <MDBox
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    bgcolor: "background.paper",
                                    height: { xs: "auto", md: "72vh" },
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                }}
                            >
                                <MDTypography variant="h6" gutterBottom>
                                    HQ Country Filter
                                </MDTypography>

                                <Autocomplete
                                    value={countrySearch}
                                    inputValue={countryInput}
                                    onInputChange={(_e, v) => setCountryInput(v)}
                                    onChange={(_e, val) => {
                                        setCountrySearch(val);
                                        if (val) {
                                            if (filters.country === val) {
                                                toggleFilter("country", val); // toggle off
                                            } else {
                                                toggleFilter("country", val); // set
                                            }
                                        } else {
                                            setCountryInput("");
                                            if (filters.country) toggleFilter("country", filters.country); // temizle
                                        }
                                    }}
                                    options={hqCountryOptions}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Search HQ Country" size="small" />
                                    )}
                                    clearOnBlur={false}
                                    autoHighlight
                                    fullWidth
                                />

                                {!!filters.country && (
                                    <Chip
                                        label={`Selected: ${filters.country}`}
                                        onDelete={() => {
                                            setCountrySearch(null);
                                            setCountryInput("");
                                            toggleFilter("country", filters.country);
                                        }}
                                    />
                                )}

                                {(filters.country || hasAnyFilter) && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => {
                                            setCountrySearch(null);
                                            setCountryInput("");
                                            clearAllFilters();
                                        }}
                                        sx={{ color: "orange", borderColor: "orange", background: "white", alignSelf: "flex-start" }}
                                    >
                                        CLEAR FILTERS
                                    </Button>
                                )}

                                <MDBox mt="auto">
                                    <MDTypography variant="button" color="text">
                                        {`Service Providers: ${filteredAgents.length} / ${data.length}`}
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                        </Grid>
                    </Grid>
                )}

                {/* GRAFİKLER */}
                <MDBox mt={4}>
                    <Grid container spacing={2}>
                        {/* Row 1 */}
                        <Grid item xs={12} md={6}>
                            <MDBox sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper", height: 360 }}>
                                <MDTypography variant="h6" gutterBottom>
                                    By HQ (Country)
                                </MDTypography>
                                <div style={{ height: 300 }}>
                                    <Bar data={hqChart} options={hqOptionsWithClick} />
                                </div>
                            </MDBox>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <MDBox sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper", height: 360 }}>
                                <MDTypography variant="h6" gutterBottom>
                                    By Region
                                </MDTypography>
                                <div style={{ height: 300 }}>
                                    <Doughnut data={regionChart} options={regionOptionsWithClick} />
                                </div>
                            </MDBox>
                        </Grid>

                        {/* Row 2 */}
                        <Grid item xs={12} md={4}>
                            <MDBox sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper", height: 360 }}>
                                <MDTypography variant="h6" gutterBottom>
                                    By Group
                                </MDTypography>
                                <div style={{ height: 300 }}>
                                    <Bar data={industryChart} options={industryOptionsWithClick} />
                                </div>
                            </MDBox>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <MDBox sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper", height: 360 }}>
                                <MDTypography variant="h6" gutterBottom>
                                    By Service
                                </MDTypography>
                                <div style={{ height: 300 }}>
                                    <Bar data={servicesChart} options={servicesOptionsWithClick} />
                                </div>
                            </MDBox>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <MDBox sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper", height: 360 }}>
                                <MDTypography variant="h6" gutterBottom>
                                    By Collobration
                                </MDTypography>
                                <div style={{ height: 300 }}>
                                    <Bar data={collobrationChart} options={collobrationOptionsWithClick } />
                                </div>
                            </MDBox>
                        </Grid>
                    </Grid>
                </MDBox>

                {/* ===== Service ProviderMeeting Status ===== */}
                <MDBox mt={6}>
                    <MDBox p={1} display="flex" justifyContent="space-between">
                        <MDTypography variant="h5" fontWeight="medium">
                            SERVICE PROVIDER MEETING STATUS
                        </MDTypography>
                    </MDBox>

                    {/* KPI CARDS */}
                    <Grid container spacing={2} sx={{ mb: 1 }}>
                        <Grid item xs={12} md={3}>
                            <StatCard
                                icon={<InsertChartOutlinedIcon fontSize="small" />}
                                title="Total Meeting"
                                value={meetingKpis.totalMeetings}
                                caption={`Avg. Days: ${meetingKpis.avgDays}`}
                                color="primary"
                                onClick={() => {
                                    clearMeetingFilters();
                                    meetingTableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <StatCard
                                icon={<EventAvailableIcon fontSize="small" />}
                                title="0–30 Days"
                                value={meetingKpis.count0_30}
                                caption="Last meeting is recent"
                                color="success"
                                onClick={() => {
                                    toggleMeetingFilter("daysBand", "0-30");
                                    meetingTableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <StatCard
                                icon={<WarningAmberIcon fontSize="small" />}
                                title="90+ Days"
                                value={meetingKpis.count90p}
                                caption="Action is required."
                                color="error"
                                onClick={() => {
                                    toggleMeetingFilter("daysBand", "90+");
                                    meetingTableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <StatCard
                                icon={<HourglassEmptyIcon fontSize="small" />}
                                title="No Date"
                                value={meetingKpis.countNoDate}
                                caption="The date should be added."
                                color="grey"
                                onClick={() => {
                                    toggleMeetingFilter("daysBand", "nodate");
                                    meetingTableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                                }}
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <MDBox sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper", height: 360 }}>
                                <MDTypography variant="h6" gutterBottom>
                                    Customer Distribution (by Meeting Count)
                                </MDTypography>
                                <div style={{ height: 300 }}>
                                    <Bar data={customerDistributionChart} options={customerDistributionOptionsWithClick} />
                                </div>
                            </MDBox>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <MDBox sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper", height: 360 }}>
                                <MDTypography variant="h6" gutterBottom>
                                    Distribution of Interview Methods
                                </MDTypography>
                                <div style={{ height: 300 }}>
                                    <Doughnut
                                        data={methodChart}
                                        options={{
                                            ...chartOptionsDoughnut,
                                            ...{ onClick: methodOptionsWithClick.onClick },
                                            plugins: { ...chartOptionsDoughnut.plugins, centerText: { title: "Meetings" } },
                                        }}
                                    />
                                </div>
                            </MDBox>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <MDBox sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper", height: 360 }}>
                                <MDTypography variant="h6" gutterBottom>
                                    Status
                                </MDTypography>
                                <div style={{ height: 300 }}>
                                    <Bar data={statusChart} options={statusOptionsWithClick} />
                                </div>
                            </MDBox>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <MDBox sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper", height: 360 }}>
                                <MDTypography variant="h6" gutterBottom>
                                    Number of Monthly Meetings
                                </MDTypography>
                                <div style={{ height: 300 }}>
                                    <Bar data={monthlyChart} options={monthlyOptionsWithClick} />
                                </div>
                            </MDBox>
                        </Grid>
                    </Grid>

                    {/* Meeting filtre chip'leri */}
                    <MDBox mt={3}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={2}>
                            <MDTypography variant="button" mr={1}>
                                Meeting Filters:
                            </MDTypography>

                            {meetingFilters.agentIds.map((id) => {
                                const a = agentMeetingStats.find((x) => x.agentId === id);
                                const label = a?.agentName || id;
                                return <Chip key={id} label={`Agent: ${label}`} onDelete={() => toggleMeetingFilter("agentIds", id)} />;
                            })}

                            {meetingFilters.method && (
                                <Chip label={`Method: ${meetingFilters.method}`} onDelete={() => toggleMeetingFilter("method", meetingFilters.method)} />
                            )}
                            {meetingFilters.status && (
                                <Chip label={`Status: ${meetingFilters.status}`} onDelete={() => toggleMeetingFilter("status", meetingFilters.status)} />
                            )}
                            {meetingFilters.month && (
                                <Chip label={`Month: ${meetingFilters.month}`} onDelete={() => toggleMeetingFilter("month", meetingFilters.month)} />
                            )}
                            {meetingFilters.daysBand && (
                                <Chip label={`Days: ${meetingFilters.daysBand}`} onDelete={() => toggleMeetingFilter("daysBand", meetingFilters.daysBand)} />
                            )}

                            {(meetingFilters.agentIds.length || meetingFilters.method || meetingFilters.status || meetingFilters.month || meetingFilters.daysBand) && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={clearMeetingFilters}
                                    sx={{ color: "orange", borderColor: "orange", background: "white" }}
                                >
                                    CLEAR MEETING FILTERS
                                </Button>
                            )}
                        </Stack>
                    </MDBox>

                    {/* Tablo */}
                    <MDBox ref={meetingTableRef} mt={2} sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper" }}>
                        <MDTypography variant="h6" gutterBottom>
                            {showDetailTable ? "Service Provider Meeting Details" : "Service Provider Meeting Status"}
                        </MDTypography>

                        <DataTable
                            table={
                                showDetailTable
                                    ? { columns: meetingDetailColumns, rows: meetingDetailRows }  // Filtre varken: tüm toplantı kayıtları
                                    : { columns: meetingStatusColumns, rows: meetingStatusRows }  // Filtre yokken: özet
                            }
                            isSorted={true}
                            entriesPerPage={
                                showDetailTable
                                    ? {
                                        defaultValue: meetingDetailRows.length || 10,
                                        entries: [meetingDetailRows.length || 10],
                                    }
                                    : { defaultValue: 10, entries: [10, 25, 50, 100] }
                            }
                            showTotalEntries
                            noEndBorder
                        />
                    </MDBox>
                </MDBox>

                {/* FİLTRE ÇİPLERİ + TABLO */}
                <MDBox mt={6} ref={tableAnchorRef}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={2}>
                        <MDTypography variant="button" mr={1}>
                            Selected Filters:
                        </MDTypography>
                        {filters.country && <Chip label={`Country: ${filters.country}`} onDelete={() => toggleFilter("country", filters.country)} />}
                        {filters.region && <Chip label={`Region: ${filters.region}`} onDelete={() => toggleFilter("region", filters.region)} />}
                        {filters.industry && <Chip label={`Industry: ${filters.industry}`} onDelete={() => toggleFilter("industry", filters.industry)} />}
                        {filters.service && <Chip label={`Service: ${filters.service}`} onDelete={() => toggleFilter("service", filters.service)} />}
                        {filters.collobration && <Chip label={`Collobration: ${filters.collobration}`} onDelete={() => toggleFilter("collobration", filters.collobration)} />}
                        {hasAnyFilter && (
                            <Button size="small" variant="outlined" onClick={clearAllFilters} sx={{ color: "orange", borderColor: "orange", background: "white" }}>
                                CLEAR FILTERS
                            </Button>
                        )}
                    </Stack>

                    <MDBox sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper" }}>
                        <MDTypography variant="h6" gutterBottom>
                            SERVICE PROVIDERS {hasAnyFilter ? "(Filtered)" : ""}
                        </MDTypography>
                        <DataTable
                            table={dataTableData}
                            isSorted={true}
                            entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50, 100] }}
                            showTotalEntries
                            noEndBorder
                        />
                    </MDBox>
                </MDBox>
            </MDBox>

            {!embedded && <Footer />}
        </>
    );

    if (embedded) return content;

    return <DashboardLayout>{content}</DashboardLayout>;

}
export default ServiceProviderDashboard;

